import type { ReactNode } from 'react';
import { Breadcrumbs } from '@/components/ds/Breadcrumbs';
import { Card } from '@/components/ds/Card';
import { PageHeader } from '@/components/ds/PageHeader';
import { Skeleton } from '@/components/ds/Skeleton';

// Loading shapes that match what loads (ported from DetectHub's ds-detecthub/,
// cascade plan 2.6; d2d-XDR's ds/ has the Skeleton primitive, not page
// shapes). DetectHub's copy wraps its own Next.js PageHeader and Breadcrumbs;
// DLP is served at / and has no section tabs, so ds/'s own are used. Each piece is drawn in the real
// component's own box, so nothing moves when the data lands:
//
//   StatTilesSkeleton   the KPI row: ds StatCard's own .rc-stat-card box (min 10rem)
//   ChartCardSkeleton   a Card with a header and a chart area
//   TableCardSkeleton   a Card with a header, a toolbar and table rows
//   CardGridSkeleton    a grid of summary cards (frameworks, repositories ...)
//   PageBodySkeleton    one of the page models below, composed from those
//   PageLoading         a page's masthead (its real title, or placeholder lines)
//                       over a PageBodySkeleton: a page's first-load state
//
// Placeholders are aria-hidden; the region announces "Loading" once. Each is
// `block`: ds Skeleton is an inline <span>, and stacked spans sit side by side.

const LINE_WIDTHS = ['72%', '58%', '81%', '64%', '76%', '52%', '68%', '60%'];

/** The KPI row, in StatCard's own box: label + icon on top, the figure under it. */
export function StatTilesSkeleton({ count = 4 }: { count?: number }) {
    return (
        <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(10rem,1fr))]" aria-hidden="true">
            {Array.from({ length: count }, (_, i) => (
                <div key={i} className="rc-stat-card">
                    <span className="rc-stat-card__head">
                        <Skeleton className="block" variant="text" width={i % 2 ? '52%' : '64%'} height={12} />
                        <Skeleton className="block" variant="rectangle" width={18} height={18} radius={4} />
                    </span>
                    <span className="rc-stat-card__main">
                        <Skeleton className="block" variant="rectangle" width={i % 3 ? 56 : 72} height={28} radius={6} />
                    </span>
                </div>
            ))}
        </div>
    );
}

function CardHeaderSkeleton({ action = false }: { action?: boolean }) {
    return (
        <Card.Header
            title={<Skeleton className="block" variant="text" width={180} height={16} />}
            description={<Skeleton className="block" variant="text" width={240} height={12} />}
            actions={action ? <Skeleton className="block" variant="rectangle" width={88} height={32} radius={8} /> : undefined}
        />
    );
}

/** A chart card: the header, then the plot area with a few axis ticks. */
export function ChartCardSkeleton({ height = 240 }: { height?: number }) {
    return (
        <Card aria-hidden="true">
            <CardHeaderSkeleton />
            <Card.Body>
                <div className="flex gap-3" style={{ height }}>
                    <div className="flex flex-col justify-between py-1">
                        {Array.from({ length: 4 }, (_, i) => <Skeleton className="block" key={i} variant="text" width={24} height={10} />)}
                    </div>
                    <Skeleton className="block" variant="rectangle" width="100%" height="100%" radius={8} />
                </div>
            </Card.Body>
        </Card>
    );
}

/** A table card: header, an optional search + filter toolbar, a column header row and rows. */
export function TableCardSkeleton({ rows = 6, columns = 4, toolbar = true, header = true }: { rows?: number; columns?: number; toolbar?: boolean; header?: boolean }) {
    const cols = Array.from({ length: columns }, (_, c) => c);
    return (
        <Card aria-hidden="true">
            {header ? <CardHeaderSkeleton /> : null}
            <Card.Body className="space-y-4">
                {toolbar ? (
                    <div className="flex flex-wrap items-center gap-3">
                        <Skeleton className="block" variant="rectangle" width={280} height={36} radius={8} />
                        <Skeleton className="block" variant="rectangle" width={64} height={28} radius={999} />
                        <Skeleton className="block" variant="rectangle" width={72} height={28} radius={999} />
                        <Skeleton className="block" variant="rectangle" width={60} height={28} radius={999} />
                    </div>
                ) : null}
                <div className="divide-y divide-[var(--d2d-border)] rounded-[var(--radius-card)] border border-[var(--d2d-border)]">
                    <div className="grid gap-4 bg-[var(--d2d-bg-secondary)] px-4 py-3" style={{ gridTemplateColumns: `2fr repeat(${columns - 1}, 1fr)` }}>
                        {cols.map((c) => <Skeleton className="block" key={c} variant="text" width={c === 0 ? '30%' : '50%'} height={10} />)}
                    </div>
                    {Array.from({ length: rows }, (_, r) => (
                        <div key={r} className="grid items-center gap-4 px-4 py-3.5" style={{ gridTemplateColumns: `2fr repeat(${columns - 1}, 1fr)` }}>
                            {cols.map((c) =>
                                c === 0 ? (
                                    <Skeleton className="block" key={c} variant="text" width={LINE_WIDTHS[r % LINE_WIDTHS.length]} height={12} />
                                ) : c === 1 ? (
                                    <Skeleton className="block" key={c} variant="rectangle" width={56} height={20} radius={999} />
                                ) : (
                                    <Skeleton className="block" key={c} variant="text" width={c % 2 ? '60%' : '44%'} height={12} />
                                ),
                            )}
                        </div>
                    ))}
                </div>
            </Card.Body>
        </Card>
    );
}

/** A grid of summary cards: an icon tile, a name, two lines and a progress rail. */
export function CardGridSkeleton({ count = 6, min = '18rem' }: { count?: number; min?: string }) {
    return (
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${min}, 1fr))` }} aria-hidden="true">
            {Array.from({ length: count }, (_, i) => (
                <Card key={i}>
                    <Card.Body className="space-y-3">
                        <div className="flex items-center gap-3">
                            <Skeleton className="block" variant="rectangle" width={36} height={36} radius={8} />
                            <div className="flex-1 space-y-2">
                                <Skeleton className="block" variant="text" width={i % 2 ? '50%' : '62%'} height={14} />
                                <Skeleton className="block" variant="text" width="38%" height={10} />
                            </div>
                        </div>
                        <Skeleton className="block" variant="text" width="88%" height={12} />
                        <Skeleton className="block" variant="rectangle" width="100%" height={6} radius={999} />
                    </Card.Body>
                </Card>
            ))}
        </div>
    );
}

/**
 * The page models DetectHub's pages follow:
 *   dashboard  KPI rows, a chart beside a side card, then a table (Executive, Overview)
 *   list       a KPI row, then a table (Vulnerabilities, Findings, Scans, Assets ...)
 *   table      a table only (Alerts, Scheduled Scans, Credentials ...)
 *   cards      a KPI row, then a grid of summary cards (Compliance, Code Security ...)
 *   detail     a KPI row, then a two-column body (a finding, an asset, an agent ...)
 */
export type PageModel = 'dashboard' | 'list' | 'table' | 'cards' | 'detail';

export function PageBodySkeleton({ model = 'list', tiles }: { model?: PageModel; tiles?: number }) {
    let body: ReactNode;
    switch (model) {
        case 'dashboard':
            body = (
                <>
                    <StatTilesSkeleton count={tiles ?? 4} />
                    <div className="grid gap-6 lg:grid-cols-3">
                        <div className="lg:col-span-2"><ChartCardSkeleton /></div>
                        <ChartCardSkeleton />
                    </div>
                    <TableCardSkeleton rows={4} toolbar={false} />
                </>
            );
            break;
        case 'table':
            body = <TableCardSkeleton rows={8} columns={5} />;
            break;
        case 'cards':
            body = (
                <>
                    <StatTilesSkeleton count={tiles ?? 4} />
                    <CardGridSkeleton />
                </>
            );
            break;
        case 'detail':
            body = (
                <>
                    <StatTilesSkeleton count={tiles ?? 4} />
                    <div className="grid gap-6 lg:grid-cols-3">
                        <div className="lg:col-span-2"><TableCardSkeleton rows={5} toolbar={false} columns={3} /></div>
                        <CardGridSkeleton count={1} min="14rem" />
                    </div>
                </>
            );
            break;
        default:
            body = (
                <>
                    <StatTilesSkeleton count={tiles ?? 4} />
                    <TableCardSkeleton />
                </>
            );
    }
    return (
        <div className="space-y-6" role="status" aria-live="polite" aria-busy="true">
            <span className="sr-only">Loading</span>
            {body}
        </div>
    );
}

/**
 * A page's first-load state: its masthead, then the body in the page's shape.
 * Pass the page's own title, description and icon so nothing changes when the
 * data lands; leave them out (a detail page whose name is the data) for
 * placeholder lines. `parent` adds breadcrumbs back to the list.
 */
export function PageLoading({
    title,
    description,
    icon,
    breadcrumbs,
    parent,
    model = 'list',
    tiles,
}: {
    title?: ReactNode;
    description?: ReactNode;
    icon?: ReactNode;
    /** The page's own breadcrumbs, when they do not depend on the data. */
    breadcrumbs?: ReactNode;
    parent?: { label: string; href: string };
    model?: PageModel;
    tiles?: number;
}) {
    return (
        <div className="space-y-6">
            <PageHeader
                breadcrumbs={breadcrumbs ?? (parent ? <Breadcrumbs items={[parent, { label: <Skeleton className="block" variant="text" width={120} height={12} aria-hidden="true" /> }]} /> : undefined)}
                icon={icon}
                title={title ?? <span className="dlp-line"><Skeleton className="block" variant="text" width={240} height={22} aria-hidden="true" /></span>}
                description={description ?? <span className="dlp-line"><Skeleton className="block" variant="text" width={320} height={12} aria-hidden="true" /></span>}
            />
            <PageBodySkeleton model={model} tiles={tiles} />
        </div>
    );
}
